import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Check, Copy, ExternalLink, Globe, Lock } from 'lucide-react'

import { Badge } from '../../ui/Badge'
import { ExternalLinkButton } from '../../ui/ExternalLinkButton'
import { accessAddress, accessHref, type IngressState, type PortAccess } from '../../core/access'

/** How long the copied check shows before the copy icon returns. */
export const COPIED_MS = 1200

export const NOT_PUBLIC_COPY = "This host hasn't set up its public HTTPS address yet, so this name doesn't open in a browser. It will once the host does."
export const CHECKING_COPY = 'Checking whether this host serves this address over HTTPS…'
export const UNREADABLE_COPY = "Couldn't read this host's settings, so the console can't tell whether this address opens in a browser. Refresh the page to try again."

/** The badge and sentence for an ingress name that is not (known to be) served over HTTPS. */
export function notPublicCopy(state: IngressState): { badge: string; sentence: string } {
  if (state === 'checking') return { badge: 'Checking', sentence: CHECKING_COPY }
  if (state === 'unreadable') return { badge: 'Unknown', sentence: UNREADABLE_COPY }
  return { badge: 'Not public yet', sentence: NOT_PUBLIC_COPY }
}
export const WAITING_COPY = 'Waiting for the host'

/**
 * A single-line value with a copy button. The value never wraps; a long one
 * scrolls sideways. When the clipboard refuses, the text is selected instead,
 * so the tenant can copy it by hand.
 */
export function CopyField({ value, label, icon, children }: { value: string; label: string; icon?: ReactNode; children?: ReactNode }) {
  const [copied, setCopied] = useState(false)
  const text = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!copied) return
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS)
    return () => window.clearTimeout(timer)
  }, [copied])
  const selectText = () => {
    const node = text.current
    const selection = window.getSelection?.()
    if (node && selection) selection.selectAllChildren(node)
  }
  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('no clipboard')
      await navigator.clipboard.writeText(value)
      setCopied(true)
    } catch {
      selectText()
    }
  }
  return <div className="access-field">
    {icon && <span className="access-field-icon" aria-hidden="true">{icon}</span>}
    <code ref={text} className="access-field-value">{value}</code>
    <button type="button" className="access-field-copy" aria-label={label} title={label} onClick={() => void copy()}>
      {copied ? <Check aria-hidden="true" size={16} /> : <Copy aria-hidden="true" size={16} />}
    </button>
    {children}
    <span className="sr-only" role="status" aria-live="polite">{copied ? 'Copied to clipboard' : ''}</span>
  </div>
}

function OpenLink({ href, name }: { href: string; name: string }) {
  return <ExternalLinkButton href={href} size="sm" aria-label={`Open ${name}`}>Open<ExternalLink aria-hidden="true" size={14} /></ExternalLinkButton>
}

/** One way into a service, as the access card shows it (plan §2.1, with D1 and D3). */
export function AccessBlock({ access, ingress = 'not-public' }: { access: PortAccess; /** What is known about the host's HTTPS; decides the not-public copy. */ ingress?: IngressState }) {
  const address = accessAddress(access)
  const href = accessHref(access)
  switch (access.kind) {
    case 'https':
      return <div className="access-block">
        <div className="access-block-head"><span className="access-block-label">Address</span><Badge tone="positive">Public · HTTPS</Badge></div>
        <CopyField value={address!} label={`Copy ${address}`} icon={<Lock size={16} />}><OpenLink href={href!} name={access.hostname} /></CopyField>
      </div>
    case 'not-public': {
      const copy = notPublicCopy(ingress)
      return <div className="access-block">
        <div className="access-block-head"><span className="access-block-label">Address</span><Badge tone="neutral">{copy.badge}</Badge></div>
        <CopyField value={address!} label={`Copy ${address}`} icon={<Globe size={16} />} />
        <p className="field-help">{copy.sentence}</p>
      </div>
    }
    case 'nodeport':
      return <div className="access-block">
        <div className="access-block-head"><span className="access-block-label">Port {access.port}/{access.protocol}</span><Badge tone="neutral">Public port {access.publicPort}</Badge></div>
        <CopyField value={address!} label={`Copy ${address}`}>{href && <OpenLink href={href} name={address!} />}</CopyField>
      </div>
    case 'pending':
      return <div className="access-block">
        <div className="access-block-head"><span className="access-block-label">{access.ingress ? 'Address' : `Port ${access.port}/${access.protocol}`}</span><Badge tone="neutral">{WAITING_COPY}</Badge></div>
        <p className="field-help">The host reports this {access.ingress ? 'address' : 'port'} once the service is set up. Until then there is nothing to connect to.</p>
      </div>
  }
}

/** The small labelled facts behind each way in: host IP, container port and protocol, public port. */
export function ConnectionDetails({ accesses, hostIp }: { accesses: readonly PortAccess[]; hostIp: string | null }) {
  return <details className="technical-disclosure access-details">
    <summary>Connection details</summary>
    <dl className="access-tiles">
      <div className="access-tile"><dt>Host IP</dt><dd>{hostIp ? <code>{hostIp}</code> : <span className="muted">Not reported by this host</span>}</dd></div>
      {accesses.map((access, index) => {
        const key = `${access.kind}-${index}`
        if (access.kind === 'nodeport') return <div key={key} className="access-tile"><dt>Port {access.port}/{access.protocol}</dt><dd>Public port <code>{access.publicPort}</code></dd></div>
        if (access.kind === 'https') return <div key={key} className="access-tile"><dt>{access.hostname}</dt><dd>Public port <code>443</code> (HTTPS)</dd></div>
        if (access.kind === 'not-public') return <div key={key} className="access-tile"><dt>{access.hostname}</dt><dd className="muted">No public port yet</dd></div>
        return <div key={key} className="access-tile"><dt>Port {access.port}/{access.protocol}</dt><dd className="muted">{WAITING_COPY}</dd></div>
      })}
    </dl>
  </details>
}
